import { z } from "zod";
import { memberDueStatusSchema } from "./collections.js";
import { billDifferenceSchema } from "./collection-amount-mode.js";
// Vai nhóm dùng CHUNG một enum với mọi bề mặt khác — khai bản thứ hai ở đây là mở đường cho hai danh
// sách vai trôi lệch. Một chiều (payment-board → groups), `groups.ts` không import ngược lại.
import { groupRoleSchema, GROUP_MONEY_REDACTED } from "./groups.js";

/* ===========================================================================
 * US-E8.F5-0056 Payment Status Board (@UC0047) — Phase 9 read-only.
 * GET /v1/collections/:id/payment-board — mọi vai (QĐ 07/09/2026); nhóm `managers_only` ⇒ member chỉ thấy dòng của
 * mình + tổng số người (`membersScope: "self"`, US-E6.F9-0873).
 *
 * Tổng đã thu / còn thiếu / kỳ vọng (từ member_dues.amountMinor + status) + list member (CÓ số tiền —
 * đây là view quản lý, khác 0047 members-status ẩn tiền). Filter status. amount_minor no-format B1.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Filter status trên board (query) — 'all' + các member_due status. */
export const paymentBoardStatusFilterSchema = z.enum([
  "all",
  "paid",
  "awaiting_confirmation",
  "pending",
  "overdue",
  "waived",
  "paid_on_behalf",
  /**
   * 🔴 US-E6.F3-0680 (02/09/2026) — ĐÓNG MỘT PHẦN. `counts.partially_paid` đã có từ 0185, nhưng
   * enum lọc thì không: board ĐẾM được nhóm người này rồi chặn 400 khi thủ quỹ bấm vào chính con
   * số vừa hiện. Lọc ở service là so sánh chuỗi thuần (`r.status === filter`), nên chỉ enum ở biên
   * đứng chắn. Thêm giá trị là ADDITIVE — không client nào đang gửi nó.
   */
  "partially_paid",
]);
export type PaymentBoardStatusFilter = z.infer<
  typeof paymentBoardStatusFilterSchema
>;

/** Query board — filter status optional. */
export const paymentBoardQuerySchema = z
  .object({
    status: paymentBoardStatusFilterSchema.optional(),
  })
  .strict();
export type PaymentBoardQuery = z.infer<typeof paymentBoardQuerySchema>;

/**
 * 1 dòng member trên board.
 *
 * 🔴🔴 QĐ người dùng 07/09/2026 — BOARD NAY MỞ CHO THÀNH VIÊN THƯỜNG (trước: `@RequireFundRoles`
 * O/A/T, member 403). Người dùng báo: ở màn Chi tiết đợt thu, member không thấy ai đã/chưa đóng.
 *
 * ⇒ Số tiền từng người nay đi qua **union theo `redacted`**, ĐÚNG khuôn `memberListItemSchema`
 * (`groups.ts`) — một placeholder `'•••'` duy nhất trên toàn API, client chỉ học một luật.
 * Trục gác là `payment_status_visibility`, cùng hàm `maySeeMemberContribution` mà `GET /members` và
 * màn Chi tiết thành viên đang dùng. KHÔNG viết luật thứ hai ở service.
 *
 * 🔴 `'•••'` chứ KHÔNG phải `0` (§3.8): `0` bịa ra một lời cáo buộc có thật — *"người này chưa đóng
 * đồng nào"*.
 */
export const paymentBoardMemberBase = {
    groupMemberId: z.string().uuid(),
    /**
     * 🔴 US-MOB.F4-0558 (@UC0041) — hàng này là của CHÍNH người đang gọi.
     *
     * Màn dùng để KHÔNG vẽ nút "Nhắc nhở" trên hàng của mình: bảng thu chỉ O/A/T mở được, nên một
     * quản trị viên chưa đóng sẽ thấy nút nhắc chính mình — vô nghĩa, và bấm vào thì tự gửi thông
     * báo cho bản thân.
     *
     * SERVER tính, không để client so id: client KHÔNG biết `groupMemberId` của chính nó trong
     * nhóm, và ca "bố hai con" (một người đại diện nhiều suất) thì so một id là sai. Server xét cả
     * `primaryUserId` lẫn `membership_accounts`.
     *
     * `.default(false)` — server cũ chưa gửi ⇒ hành vi y như trước (vẫn hiện nút), 0 regress.
     */
    isSelf: z.boolean().default(false),
    /**
     * 🔴 FUND-DEBT-SELF-CONFIRM (@UC9264 · §3.7 · QĐ PO 06/10 + QĐ điều phối) — khoản "nợ quỹ" (đợt `fund_debt`) được NGƯỜI NỢ TỰ XÁC NHẬN (ngoại lệ
     * hẹp: họ là quản lý DUY NHẤT của nhóm) — có bút toán CONTRIBUTION nhãn `self_one_party` còn hiệu lực. Ghi CÔNG KHAI ở kênh XEM (đúng luật hiển
     * thị hiện có của bề mặt này), mobile hiện nhãn "Tự xác nhận". `false` cho mọi khoản khác. `.default(false)` — server cũ chưa gửi ⇒ như trước.
     */
    selfConfirmed: z.boolean().default(false),
    memberName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /**
     * 🔴 US-E24.F46-0460 (@UC0060 · @UC9659) — phần ĐÃ ĐÓNG của due này (STRING minor, B1).
     *
     * VÌ SAO CẦN: bảng thu trước đây chỉ trả `amountMinor` (số PHẢI đóng), nên một người đã đóng
     * 1.120.000/1.200.000 không có chỗ nào để client hiện con số đó — họ nằm nguyên trong mục
     * "chưa đóng" như thể chưa trả đồng nào. Bug thật báo cáo 19/08/2026, nhóm `3ea4be49`:
     * sổ cái GHI ĐÚNG cho suất, chỉ màn hình không nói ra.
     *
     * `"0"` khi chưa đóng gì — KHÔNG nullable: thiếu số ≠ số 0, và ở đây luôn có số thật.
     */
    /**
     * 🔴 US-MOB.F4-0521 (@UC0057) — phần NGƯỜI NÀY còn thiếu ở due này (STRING minor).
     *
     * VÌ SAO SERVER PHẢI TRẢ, thay vì client tự lấy `amountMinor - paidAmountMinor`:
     * `apps/mobile/CLAUDE.md` §4 M1 cấm client làm MỌI phép tính trên tiền, và nói thẳng cách
     * thoát — *"thiếu tổng/số dư/phần chia thì sửa contract để server trả"*. Đây đúng ca đó.
     *
     * VÌ SAO CẦN: sheet "Nộp quỹ" điền sẵn số tiền khi thủ quỹ ghi hộ một phụ huynh. Nếu điền
     * SUẤT CHUNG của đợt, thì người **được miễn giảm** hoặc **đã đóng dở** sẽ bị đề xuất một con
     * số lớn hơn thứ họ thực nợ — và người bấm là thủ quỹ, không phải người trong cuộc, nên rất
     * dễ bấm qua. Có số này thì gợi ý bám đúng từng người.
     *
     * Không âm: đã đóng dư ⇒ `"0"`, KHÔNG phải số âm (số âm là một nghĩa khác — "quỹ nợ lại").
     */
    status: memberDueStatusSchema,
    /** member_due id — cho tap 'chờ duyệt' → màn duyệt (0044 wiring). */
    memberDueId: z.string().uuid(),
    /**
     * US-E24.F9-0358-S02 (@UC9589) — lần nhắc GẦN NHẤT của CHÍNH due này (ISO), null nếu chưa nhắc.
     * 🔴 DẪN XUẤT từ `outbox_events` (gộp nhắc thủ công + nhắc worker), **KHÔNG** phải cột trên
     * `member_dues` — thêm cột là tạo nguồn thứ hai sẽ trôi khỏi nhau.
     * 🔴 CHỈ phơi ở board của O/A/T. Đây là màn quản lý; member KHÔNG thấy "bạn đã bị nhắc 3 lần"
     * (§7 P5.3 — kênh NHẮC là riêng tư, app không đóng vai kẻ bêu tên).
     */
    lastRemindedAt: z.string().datetime().nullable(),
    /**
     * US-E24.F38-0452 (@UC0060) — THỜI ĐIỂM ĐÓNG của chính due này (ISO), `null` nếu chưa đóng.
     *
     * Cột `member_dues.paid_at` đã tồn tại từ lâu, chỉ chưa bao giờ được phơi ra. Màn "Chi tiết đợt
     * thu" cần nó để trả lời câu hỏi đầu tiên mọi thủ quỹ hỏi: *"ai đã đóng, và đóng hôm nào?"* —
     * thiếu nó thì danh sách "đã đóng" chỉ là một tập tên, không đối chiếu được với sao kê ngân hàng.
     *
     * 🔴 CHỈ ở board của O/A/T, KHÔNG ở `members-status` (kênh XEM của member) — mirror `lastRemindedAt`
     * ngay trên. Giờ-đóng của người khác là dữ liệu quản lý, không phải thứ cả nhóm cần thấy.
     */
    paidAt: z.string().datetime().nullable(),
    /**
     * US-E24.F38-0452 (@UC0060) — VAI của thành viên trong nhóm. Màn chi tiết hiện huy hiệu vai
     * (chủ nhóm / thủ quỹ) cạnh tên; thiếu field này client chỉ có hai đường, cả hai đều sai:
     * bỏ huy hiệu, hoặc gọi thêm một request danh sách thành viên rồi tự ghép — mà ghép ở client thì
     * sai ngay từ trang thứ hai của danh sách phân trang.
     */
    role: groupRoleSchema,
};

/** Ba số tiền CỦA MỘT NGƯỜI trên board — khai một chỗ để hai nhánh union không trôi khỏi nhau. */
const paymentBoardMoney = {
  amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
};

export const paymentBoardMemberSchema = z.discriminatedUnion("redacted", [
  z.object({ ...paymentBoardMemberBase, redacted: z.literal(false), ...paymentBoardMoney }).strict(),
  z
    .object({
      ...paymentBoardMemberBase,
      redacted: z.literal(true),
      amountMinor: z.literal(GROUP_MONEY_REDACTED),
      paidAmountMinor: z.literal(GROUP_MONEY_REDACTED),
      outstandingMinor: z.literal(GROUP_MONEY_REDACTED),
    })
    .strict(),
]);
export type PaymentBoardMember = z.infer<typeof paymentBoardMemberSchema>;

/**
 * 🔴 US-E6.F9-0873 (@UC0047 @UC9581 · QĐ PO 30/09/2026) — PHẠM VI danh sách người của board.
 *
 *  · `all`  — mọi dòng (vai O/A/T, hoặc quỹ của đợt đặt `payment_status_visibility = all_members`);
 *  · `self` — CHỈ dòng của suất người gọi đứng tên/đại diện, kèm TỔNG SỐ người. Đây là kênh XEM của nhóm
 *    `managers_only` — CÙNG luật `members-status` đang 403 (một vị-từ: `othersPaymentStatusVisible`).
 *
 * VÌ SAO: security-reviewer PR #1097 (H1) — member ghép `GET /funds/:id/transactions` (id + số) → `GET
 * /transactions/:id` (`memberDueId`) → board (`memberDueId` → tên) ra CHÍNH XÁC số từng người đóng, trong khi
 * `members-status` đã 403 cho cùng cấu hình. Số `'•••'` trên dòng không cứu được gì khi dòng vẫn còn đó.
 */
export const PAYMENT_BOARD_MEMBERS_SCOPE = {
  ALL: "all",
  SELF: "self",
} as const;
export type PaymentBoardMembersScope =
  (typeof PAYMENT_BOARD_MEMBERS_SCOPE)[keyof typeof PAYMENT_BOARD_MEMBERS_SCOPE];

/**
 * 1 dòng ở nhánh `self` — dòng của CHÍNH người gọi. Khoá bằng kiểu những gì nhánh này KHÔNG được mang:
 *  · `isSelf: true` — một dòng người khác lọt vào ⇒ `.parse` ném (service quên lọc = 500, không rò lặng);
 *  · `paidAt: null` · `lastRemindedAt: null` — hai field CHỈ ở board của O/A/T (người xem ở nhánh này không phải O/A/T);
 *  · `redacted: false` — số của CHÍNH mình luôn thấy (`maySeeMemberContribution`: `isSelf` ⇒ true).
 */
export const paymentBoardSelfMemberSchema = z
  .object({
    ...paymentBoardMemberBase,
    isSelf: z.literal(true),
    paidAt: z.null(),
    lastRemindedAt: z.null(),
    redacted: z.literal(false),
    ...paymentBoardMoney,
  })
  .strict();
export type PaymentBoardSelfMember = z.infer<typeof paymentBoardSelfMemberSchema>;

/**
 * Phần chung của hai nhánh. Tổng đã thu (paid+paid_on_behalf) / còn thiếu (pending+overdue+awaiting) / kỳ vọng
 * (tổng non-waived). `members` = list đã filter (nếu có status). `expectedMinor` KHÔNG gồm waived
 * (miễn không kỳ vọng thu). Tiền STRING minor unit (B1).
 *
 * ⚠️ QĐ PO 30/09/2026 — ba tổng tiền KHÔNG che theo `collection_amount_visibility` (rủi ro đã chấp nhận: ở nhóm tự bật ẩn
 * tổng, member gọi API trực tiếp lấy tổng − phần của mình suy được phần người khác; mobile không hiển thị ba số này; cài đặt
 * không đổi được từ app). Đừng "vá cho đúng" khi chưa có QĐ mới.
 */
/**
 * US-E16.F1-1023 (@UC0464 · QĐ người dùng 22/10) — đợt thu tạo THEO HÓA ĐƠN: tổng hóa đơn + chênh lệch + ảnh (nếu có). `null` = đợt thường.
 *
 * Ở BẢNG THU (mọi vai xem được đợt) chứ không chỉ ở chi tiết khoản của mình: quản lý không thuộc diện phải đóng (ghế người lớn ở nhóm phụ
 * huynh) cũng phải đọc lại được hóa đơn đã đính kèm (review S4 MEDIUM-1). `difference` so `expectedMinor` (cùng cấp hiển thị với ba tổng
 * ở dưới — QĐ PO 30/09) với tổng hóa đơn, SERVER tính (`billDifference`) — app không tự trừ. Miễn khoản sau khi tạo ⇒ `expectedMinor` giảm
 * ⇒ chênh lệch tăng: đúng nghĩa «quỹ đang kỳ vọng thu ít hơn hóa đơn bao nhiêu». URL ảnh ngắn hạn; KEY R2 không rời API.
 */
export const paymentBoardBillSchema = z
  .object({
    totalMinor: z.string().regex(/^[1-9]\d*$/u),
    difference: billDifferenceSchema,
    receiptUrl: z.string().url().nullable(),
    receiptUrlExpiresAt: z.string().datetime().nullable(),
  })
  .strict();

const paymentBoardResponseBase = {
  collectionId: z.string().uuid(),
  currency: z.string().length(3),
  /** Tổng đã thu (paid + paid_on_behalf). */
  collectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  /** Còn thiếu (pending + overdue + awaiting_confirmation). */
  outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  /** Kỳ vọng thu (tổng non-waived = collected + outstanding). */
  expectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  /**
   * 🔴 US-E6.F9-0873 (@UC0047 · khuôn `collection-period-status`) — TỔNG SỐ NGƯỜI, đếm bằng MỘT bộ đếm
   * `tallyMemberProgress` trên mọi dòng của đợt (không phụ thuộc `?status=` lẫn `membersScope`). Ba số chia
   * trọn các dòng KHÔNG `waived`: `paidCount + partiallyPaidCount + unpaidCount = totalMembers` của `/status`.
   *  · `paidCount` — đóng đủ (paid + paid_on_behalf) = `paidMembers` của `/collections/:id/status`;
   *  · `partiallyPaidCount` — đóng dở (tiền đã vào một phần);
   *  · `unpaidCount` — chưa đóng đồng nào hoặc mới khai chờ xác nhận (pending + overdue + awaiting_confirmation).
   * SERVER đếm: ở nhánh `self` client chỉ cầm dòng của mình — tự đếm từ `members[]` là nói sai.
   */
  paidCount: z.number().int().nonnegative(),
  partiallyPaidCount: z.number().int().nonnegative(),
  unpaidCount: z.number().int().nonnegative(),
  bill: paymentBoardBillSchema.nullable(),
};

/**
 * Đếm theo status cho chip filter — trên MỌI dòng HIỂN THỊ (không phụ thuộc `?status=`).
 * 🔴 US-E6.F9-0863: dòng của suất rác đã dọn (suất `removed` ∧ `waived` ∧ paid 0) không được trả
 * nên cũng KHÔNG được đếm — chip và danh sách luôn khớp nhau. Dòng đó đóng góp 0 vào mọi tổng tiền.
 * 🔴 US-E6.F9-0873 (review #1100 security L1): CHỈ ở nhánh `all`. Bảy trạng thái ("1 người được miễn", "1 người được
 * nộp hộ") ở nhóm nhỏ là đủ để đoán đích danh — nhánh `self` chỉ có ba số đủ · dở · chưa.
 */
const paymentBoardStatusCountsSchema = z.object({
  paid: z.number().int(),
  awaiting_confirmation: z.number().int(),
  pending: z.number().int(),
  overdue: z.number().int(),
  waived: z.number().int(),
  paid_on_behalf: z.number().int(),
  // US-E16.F4-0185 (@UC9241): đóng một phần (còn nợ dư). Default 0 để response cũ vẫn hợp lệ.
  partially_paid: z.number().int().default(0),
});

/**
 * Response board — union theo `membersScope` (US-E6.F9-0873). Nhánh `all` giữ NGUYÊN hình dạng dòng cũ (union
 * `redacted` theo trục tiền) + `counts`; nhánh `self` chỉ nhận [paymentBoardSelfMemberSchema], không `counts`.
 */
export const paymentBoardResponseSchema = z.discriminatedUnion("membersScope", [
  z
    .object({
      ...paymentBoardResponseBase,
      membersScope: z.literal(PAYMENT_BOARD_MEMBERS_SCOPE.ALL),
      counts: paymentBoardStatusCountsSchema,
      members: z.array(paymentBoardMemberSchema),
    })
    .strict(),
  z
    .object({
      ...paymentBoardResponseBase,
      membersScope: z.literal(PAYMENT_BOARD_MEMBERS_SCOPE.SELF),
      members: z.array(paymentBoardSelfMemberSchema),
    })
    .strict(),
]);
export type PaymentBoardResponse = z.infer<typeof paymentBoardResponseSchema>;
